import { motion } from "framer-motion";
import { 
  Library, 
  Sparkles, 
  ShieldCheck,
  Zap
} from "lucide-react";
import type { ReactNode } from "react";

interface FeatureCardProps {
  icon: ReactNode;
  title: string;
  outcome: string;
  description: string;
  prominent?: boolean;
}

const FeatureCard = ({ icon, title, outcome, description }: FeatureCardProps) => (
  <div
    className="group relative landing-card border border-white/[0.08] rounded-lg text-center p-7 md:p-10 lg:p-12 transition-all duration-300 ease-out hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_0_20px_-4px_hsl(var(--primary)/0.15)] h-full flex flex-col"
  >
    <div className="mb-7 flex justify-center text-primary/60">
      {icon}
    </div>
    
    <h3 className="font-medium text-foreground/90 mb-3 text-base md:text-lg">
      {title}
    </h3>
    
    <div className="h-px mb-5 mx-auto w-10 bg-primary/35" />
    
    <p className="font-medium text-foreground/70 mb-4 text-base md:text-lg">
      {outcome}
    </p>
    
    <p className="font-normal text-white/60 leading-[1.75] text-sm mt-auto">
      {description}
    </p>
  </div>
);

const cards: (Omit<FeatureCardProps, "icon"> & { iconEl: ReactNode })[] = [
  {
    iconEl: <Library className="w-6 h-6" />,
    title: "Your Knowledge Vault.",
    outcome: "Your past proposals, case studies, and company content — searchable by AI in seconds.",
    description: "Every winning response, case study, and approach lives in one searchable place. No more hunting through shared drives before a deadline."
  },
  {
    iconEl: <Sparkles className="w-6 h-6" />,
    title: "AI Drafting",
    outcome: "First draft written from your own winning content — not generic AI output.",
    description: "Generate compliant first drafts from your proven content. Your voice, your answers, assembled automatically.",
    prominent: true
  },
  {
    iconEl: <ShieldCheck className="w-6 h-6" />,
    title: "You Stay in Control",
    outcome: "AI assists. Your team decides.",
    description: "Sorvaine handles analysis and matching. Your team writes, reviews, and approves every word before it goes out."
  }
];

export function FeatureOutcomesGrid() {
  return (
    <section id="features" className="pt-[80px] md:pt-[100px] lg:pt-[120px] pb-[112px] md:pb-[128px] lg:pb-[144px] scroll-mt-20">
      <div className="max-w-[1200px] mx-auto px-6">

        {/* Section Header */}
        <motion.div
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16 md:mb-20"
        >
          <div className="flex items-center justify-center gap-4 mb-8">
            <span className="text-sm font-normal tracking-[0.12em] uppercase text-muted-foreground">How It Works</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-medium text-foreground/95 tracking-tight leading-[1.15]">
            AI that works from your knowledge, <span className="text-foreground/[0.7]">not a blank page.</span>
          </h2>
          
          <p className="mt-8 text-base md:text-lg font-normal text-white/70 max-w-xl mx-auto leading-[1.75]">
            Sorvaine's AI searches your past wins, assembles the best answers, and writes the first draft — you refine and submit.
          </p>
        </motion.div>

        {/* Hero Card - Full Width */}
        <div className="mt-16 mb-4 md:mb-6 max-w-[960px] mx-auto">
          <div className="group relative landing-card border border-white/[0.08] rounded-lg text-center py-10 px-6 md:py-14 md:px-16 lg:py-16 lg:px-20 transition-all duration-200 ease-out hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_0_20px_-4px_hsl(var(--primary)/0.15)]">
            <div className="mb-6 flex justify-center text-primary">
              <Zap className="w-6 h-6" />
            </div>
            
            <h3 className="font-medium text-foreground/90 mb-3 text-lg md:text-xl">
              From first read to final submission — without starting from scratch
            </h3>
            
            <div className="h-px mb-5 mx-auto w-14 bg-primary/45" />
            
            <p className="font-normal text-muted-foreground leading-[1.75] text-base max-w-[90%] md:max-w-[60%] mx-auto">
              Upload a tender, RFP, or RFQ. Requirements are extracted and matched to your best answers instantly. A complete first draft. — ready in minutes. What took days now takes hours.
            </p>
          </div>
        </div>

        {/* 3-Column Grid — staggered heights, center card taller */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-stretch">
          {cards.map((card) => (
            <div key={card.title}>
              <FeatureCard icon={card.iconEl} title={card.title} outcome={card.outcome} description={card.description} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
